import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { host, baseUrl, tenant, userApi } from '../../../services/endpoints';

import {
    calculateFuelOrderPriceService,
    contactUsService,
    createFuelOrderService,
    downloadDocumentPDFService,
    getUserAccountService,
    openDocumentPDFService,
    updateCurrentUserPasswordService,
    exportUserDeliveryService,
    exportUserTransactionService
} from '../../../services/userServices';

import { CreateFuelOrderArgs, ExportArgs, UserAccountsState } from './types';
import { ContactUsArgs } from 'MyAccountPlus-UIComponents';

const initialState: UserAccountsState = {
    loading: false,
    error: "",
    success: "",
    orderSuccess: "",
    contactSuccess: "",
    download: "",
    orderPrice: {
        total_price: ''
    },
    overviewAccounts: [],
    filteredTabs: [],
    account: {
        account_id: '',
        balance_30_days: '',
        balance_60_days: '',
        balance_90_days: '',
        balance_over_90_days: '',
        billing_address: {
            street1: '',
            city: '',
            state: '',
            zipcode: '',
            country: 'US'
        },
        budget_balance: '',
        budget_paid_YTD: '',
        budget_payment: '',
        display_name: '',
        email: '',
        first_name: '',
        home_phone: '',
        is_budget_plan: false,
        is_cancelled: false,
        is_deleted: false,
        last_name: '',
        last_payment_amount: '',
        last_payment_date: '',
        non_budget_balance: '',
        account_balance: '',
        amount_due: '',
        budget_quantity_expected: {
            unit: '',
            quantity: 0
        },
        account_owner: {
            email: '',
            first_name: '',
            last_name: '',
            user_id: '',
            home_phone: '',
            display_name: '',
            alerts: {
                delivery_reminders: false,
                new_documents: false,
                schedule_reminders: false
            }
        },
        locations: [],
        alerts: {
            delivery_reminders: false,
            new_documents: false,
            schedule_reminders: false
        },
        budget_past_due: null,
        budget_total_due: null,
        budget_quantity_used_YTD: null,
        last_statement_date: null,
        last_statement_amount: null,
        budget_planned_YTD: null,
        is_fuel: false,
        is_payment: false,
        is_records: false,
        is_drops: false
    },
    drops: {
        drop_id: '',
        drop_name: '',
        is_fixed_price: false,
        last_delivery_date: '',
        last_delivery_quantity: {
            unit: '',
            quantity: 0
        },
        next_delivery_date: '',
        price: '',
        product_code: '',
        product_name: '',
        product_type: '',
        product_quantity_purchase_YTD: {
            unit: '',
            quantity: 0
        },
        effective_date: null,
        end_date: null
    },
    location: {
        account_id: '',
        location_number: '',
        address: {
            street1: '',
            city: '',
            state: '',
            zipcode: ''
        },
        drops: [],
        first_name: '',
        last_name: ''
    },
}

export const getUserAccountsThunk = createAsyncThunk("userAccountSlice/getUserAccountsThunk", async (_, thunkAPI) => {
    try {
        const { data } = await getUserAccountService();
        return { data };
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + error.response.data.detail || "Unauthorized");
    }
});

export const contactUsThunk = createAsyncThunk("userAccountSlice/contactUsThunk", async (args: ContactUsArgs, thunkAPI) => {
    try {
        const { data } = await contactUsService(args);
        return { data };
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + error.response.data.detail || "Unauthorized");
    }
});

export const updateCurrentUserPasswordThunk = createAsyncThunk("userAccountSlice/updateCurrentUserPasswordThunk", async (args: { new_password: string, user_key: string }, thunkAPI) => {
    try {
        const { data } = await updateCurrentUserPasswordService({ new_password: args.new_password }, args.user_key);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + error.response.data.detail || "Unauthorized");
    }
});

export const downloadDocumentThunk = createAsyncThunk("userAccountSlice/downloadDocumentThunk", async (args: {
    account_id: string;
    key: string;
}, thunkAPI) => {
    try {
        const response = await downloadDocumentPDFService(args.key, args.account_id);
        return response.data; // Return the blob data directly
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error;
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + error.response.data.detail || "Unauthorized");
    }
});

export const openDocumentThunk = createAsyncThunk("userAccountSlice/openDocumentThunk", async (args: {
    account_id: string;
    key: string;
}, thunkAPI) => {
    try {
        const { data } = await downloadDocumentPDFService(args.key, args.account_id);
        const blobUrl = window.URL.createObjectURL(data);
        return { url: blobUrl };
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error;
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + error.response.data.detail || "Unauthorized");
    }
});

export const downloadTransactionThunk = createAsyncThunk("userAccountSlice/downloadTransactionThunk", async (args: ExportArgs, thunkAPI) => {
    try {
        const response = await exportUserTransactionService(args)
        return response
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + error.response.data.detail || "Unauthorized");
    }
});

export const downloadDeliveriesThunk = createAsyncThunk("userAccountSlice/downloadDeliveriesThunk", async (args: ExportArgs, thunkAPI) => {
    try {
        const response = await exportUserDeliveryService(args)
        return response
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + error.response.data.detail || "Unauthorized");
    }
});

export const createFuelOrderThunk = createAsyncThunk("userAccountSlice/createFuelOrderThunk", async (args: CreateFuelOrderArgs, thunkAPI) => {
    try {
        const { data } = await createFuelOrderService(args);
        return { data };
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error
        }
        if (error.response.data?.failure) {
            return thunkAPI.rejectWithValue(error.response.data.failure.message);
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + error.response.data.detail || "Unauthorized");
    }
});

export const calculateFuelOrderPriceThunk = createAsyncThunk("userAccountSlice/calculateFuelOrderPriceThunk", async (args: CreateFuelOrderArgs, thunkAPI) => {
    try {
        const { data } = await calculateFuelOrderPriceService(args);
        return { data };
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + error.response.data.detail || "Unauthorized");
    }
});

export const userAccountSlice = createSlice({
    name: 'userAccountSlice',
    initialState,
    reducers: {
        errorReducer: (state) => {
            state.error = ""
        },
        successReducer: (state) => {
            state.success = ""
        },
        orderSuccessReducer: (state) => {
            state.orderSuccess = ""
        },
        contactSuccessReducer: (state) => {
            state.contactSuccess = ""
        },
        accountsReducer: (state, action) => {
            const userAccount = state.overviewAccounts.find(item => item.account_id === action.payload);
            state.account = userAccount ? userAccount : initialState.account;
        },
        dropsReducer: (state, action) => {
            const userAccount = state.overviewAccounts.find(item => item.account_id === action.payload.accountId);
            const userLocation = userAccount?.locations.find(item => item.location_number === action.payload.location_number)
            state.location = userLocation ? userLocation : initialState.location;
            const productDrop = userLocation && userLocation?.drops.find(item => item.drop_id === action.payload.drop_id);
            state.drops = productDrop ? productDrop : initialState.drops;
        },
        clearDownloadReducer: (state) => {
            state.error = "";
            state.download = "";
            state.loading = false;
        },
        clearState: (state) => {
            return {
                ...state,
                ...initialState
            }
        },
        filteredTabsReducer: (state, action) => {
            state.filteredTabs = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getUserAccountsThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(getUserAccountsThunk.fulfilled, (state, action) => {
                state.overviewAccounts = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(getUserAccountsThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(contactUsThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(contactUsThunk.fulfilled, (state, action) => {
                state.contactSuccess = "Your message was sent. We will reply to you by email as soon as possible."
                state.loading = false
                state.error = ""
            })
            .addCase(contactUsThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(updateCurrentUserPasswordThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(updateCurrentUserPasswordThunk.fulfilled, (state, action) => {
                state.success = "Password updated successfully!"
                state.loading = false
                state.error = ""
            })
            .addCase(updateCurrentUserPasswordThunk.rejected, (state, action) => {
                state.error = action.error.message || action.payload as string || "";
                state.loading = false
            })
            .addCase(downloadDocumentThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(downloadDocumentThunk.fulfilled, (state, action) => {
                state.download = action.payload.key;
                state.loading = false
                state.error = ""
            })
            .addCase(downloadDocumentThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(openDocumentThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(openDocumentThunk.fulfilled, (state, action) => {
                state.success = "Document opened successfully!";
                state.loading = false
                state.error = ""
            })
            .addCase(openDocumentThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(createFuelOrderThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(createFuelOrderThunk.fulfilled, (state, action) => {
                state.orderSuccess = "Order created successfully!"
                state.success = "Order created successfully!"
                state.loading = false
                state.error = ""
            })
            .addCase(createFuelOrderThunk.rejected, (state, action) => {
                state.error = action.error.message || action.payload as string || "";
                state.loading = false
                state.orderSuccess = ""
                state.success = ""
            })
            .addCase(calculateFuelOrderPriceThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(calculateFuelOrderPriceThunk.fulfilled, (state, action) => {
                state.orderPrice = action.payload.data;
                state.loading = false
                state.error = ""
            })
            .addCase(calculateFuelOrderPriceThunk.rejected, (state, action) => {
                state.error = action.error.message || action.payload as string || "";
                state.loading = false
            })
            .addCase(downloadTransactionThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(downloadTransactionThunk.fulfilled, (state, action) => {
                state.success = "Data exported successfully!";
                state.loading = false
                state.error = ""
            })
            .addCase(downloadTransactionThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(downloadDeliveriesThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(downloadDeliveriesThunk.fulfilled, (state, action) => {
                state.success = "Data exported successfully!";
                state.loading = false
                state.error = ""
            })
            .addCase(downloadDeliveriesThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
    },
})

export const userAccountActions = userAccountSlice.actions
export default userAccountSlice.reducer;